import React, { useState, forwardRef } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useCountUp } from 'use-count-up'
import { Medium, MqTablet, MqDesktop } from 'streamr-ui/css/consts'
import useIsMounted from '~hooks/useIsMounted'

const Shape = styled.div`
    border-radius: 8px;
    height: 48px;
    overflow: hidden;
    position: relative;

    ${({ overlay }) => !!overlay && css`
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    `}

    @media ${MqDesktop} {
        height: 64px;
    }
`

const Track = styled(Shape)`
    background-color: #ffffff;
`

const BorderedShape = styled(Shape)`
    border: 1px solid #e7e7e7;
    box-sizing: border-box;
`

const Value = styled.div`
    align-items: center;
    display: flex;
    font-size: 12px;
    font-weight: 500;
    height: 100%;
    padding: 0 12px;

    @media ${MqTablet} {
        font-size: 14px;
        padding: 0 16px;
    }

    @media ${MqDesktop} {
        font-size: 18px;
        padding: 0 24px;
    }
`

const CurrentValue = styled(Value)`
    background-color: #0324ff;
    color: #ffffff;
`

const Tick = styled.div`
    background-color: #e7e7e7;
    bottom: 0;
    height: 8px;
    left: ${({ pos = 0.5 }) => `${pos * 100}%`};
    position: absolute;
    width: 1px;

    @media ${MqTablet} {
        height: 10px;
    }

    @media ${MqDesktop} {
        height: 16px;
    }
`

const Width = styled.div`
    height: 100%;
    overflow: hidden;

    ${Tick} {
        background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.3));
    }
`

const ProgressBar = styled.div`
    position: relative;
`

const Heading = styled.div`
    align-items: center;
    display: flex;
    font-size: 12px;
    font-weight: 500;
    line-height: 1em;
    margin-bottom: 0.75em;

    > div:first-child {
        flex-grow: 1;
    }

    @media ${MqTablet} {
        font-size: 14px;
    }

    @media ${MqDesktop} {
        font-size: 16px;
    }
`

const Supply = styled.div`
    color: #a3a3a3;
    font-size: 10px;
    text-transform: uppercase;

    @media ${MqTablet} {
        font-size: 12px;
    }
`

const Hub = styled.div`
    color: #a3a3a3;
    font-size: 10px;
    font-weight: ${Medium};
    letter-spacing: 0.04em;
    text-transform: uppercase;

    @media ${MqTablet} {
        font-size: 12px;
    }
`

const HubItem = styled.div``

const animation = keyframes`
  0% {
    left: 0%;
    right: 100%;
  }

  33% {
    left: 0%;
    right: 0%;
  }

  67% {
    left: 0%;
    right: 0%;
  }

  100% {
    left: 100%;
    right: 0%;
  }
`

const LoadingBar = styled.div`
  background-color: #f3f3f3;
  height: 2px;
  position: relative;
  transition: opacity 0.3s ease-out;
  width: 100%;
  will-change: opacity;

  ::after {
    animation: 1200ms infinite;
    animation-name: ${animation};
    background: #0324ff;
    content: '';
    display: block;
    height: 2px;
    left: 0;
    position: absolute;
    right: 100%;
    will-change: left, right;
  }
`

const Loader = styled.div`
    align-items: center;
    background-color: #f8f8f8;
    border-radius: 8px;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    transition: 200ms opacity;
    width: 100%;
    z-index: 1;

    ${LoadingBar} {
        display: none;
        max-width: 240px;
    }

    ${({ loading }) => !!loading && css`
        opacity: 1;
        pointer-events: auto;

        ${LoadingBar} {
            display: flex;
        }
    `}
`

const UnstyledProgressCounter = forwardRef(function UnstyledProgressCounter({
    className,
    value = 0.0,
    supply = '1B DATA',
    title = 'Migration completion',
    children,
    loading = false,
    style,
}, ref) {
    const [recentProgress, setRecentProgress] = useState(0)

    const isMounted = useIsMounted()

    const { value: progressCounter } = useCountUp({
        autoResetKey: value,
        decimalPlaces: 1,
        duration: 3,
        end: value * 100,
        isCounting: !loading,
        start: recentProgress,
        onComplete: () => {
            if (isMounted()) {
                setRecentProgress(value * 100)
            }
        },
    })

    const percentValue = `${progressCounter}%`

    return (
        <div className={className} style={style} ref={ref}>
            <Loader loading={loading ? 'loading' : undefined}>
                <LoadingBar />
            </Loader>
            <Heading>
                <div>{title}</div>
                <Supply>{supply}</Supply>
            </Heading>
            <ProgressBar>
                <Track>
                    <Value>{percentValue}</Value>
                    <Tick pos={0.25} />
                    <Tick pos={0.5} />
                    <Tick pos={0.75} />
                </Track>
                <BorderedShape overlay />
                <Shape overlay>
                    <Width
                        style={{
                            width: percentValue,
                        }}
                    >
                        <CurrentValue>{percentValue}</CurrentValue>
                        <Tick pos={0.25} />
                        <Tick pos={0.5} />
                        <Tick pos={0.75} />
                    </Width>
                </Shape>
            </ProgressBar>
            {children}
        </div>
    )
})

const ProgressCounter = styled(UnstyledProgressCounter)`
    background-color: #fafafa;
    border-radius: 8px;
    padding: 24px;
    position: relative;

    @media ${MqDesktop} {
        padding: 40px 32px;
    }
`

Object.assign(ProgressCounter, {
    Hub,
    HubItem,
})

export default ProgressCounter
